import { useCallback, useEffect, useState } from "react";
import { IconTrendingDown, IconTrendingUp } from "@tabler/icons-react";
import { axiosInstance } from "../../utils/axiosInstance";
import PageContainer from "../layouts/PageContainer";
import {
  Card,
  EmptyState,
  ErrorBanner,
  LoadingPanel,
  PageHeader,
  RefreshButton,
} from "../ui";
import ReportsDateFilter from "./ReportsDateFilter";
import ReportsSubNav from "./ReportsSubNav";
import { SalesByStorePayload } from "./reportsTypes";

function daysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().split("T")[0];
}

function formatMoney(n: number): string {
  return `£${n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

function formatPct(ratio: number | null): string {
  if (ratio == null) return "—";
  const pct = Math.round(ratio * 1000) / 10;
  return `${pct > 0 ? "+" : ""}${pct}%`;
}

export default function SalesByStoreReport() {
  const [from, setFrom] = useState(daysAgo(29));
  const [to, setTo] = useState(daysAgo(0));
  const [data, setData] = useState<SalesByStorePayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (f: string, t: string) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axiosInstance.get<SalesByStorePayload>("/reports/sales-by-store", {
        params: { from: f, to: t },
      });
      setData(res.data);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || "Failed to load sales report.");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(from, to);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyRange = (f: string, t: string) => {
    setFrom(f);
    setTo(t);
    load(f, t);
  };

  const worstLocationId =
    data && data.rows.length
      ? data.rows.reduce((a, b) => (b.totalSales < a.totalSales ? b : a)).locationId
      : null;

  return (
    <PageContainer>
      <PageHeader
        title="Sales by store"
        subtitle={
          data ? `${data.range.from} to ${data.range.to} · vs the same-length prior period` : undefined
        }
        actions={<RefreshButton onClick={() => load(from, to)} loading={loading} />}
      />

      <div className="sq-stack">
        <ReportsSubNav />
        <ReportsDateFilter from={from} to={to} onApply={applyRange} loading={loading} />

        {error ? <ErrorBanner onRetry={() => load(from, to)}>{error}</ErrorBanner> : null}
        {loading && !data ? (
          <Card>
            <LoadingPanel label="Loading sales" />
          </Card>
        ) : null}

        {data ? (
          <Card
            flush
            title="Ranked by total sales"
            subtitle="Highlighted row is the lowest-selling store this period. Nayax card transactions matched to each store by location pin."
            aria-label="Sales by store"
          >
            {data.rows.length === 0 ? (
              <EmptyState title="No sales data for this range" />
            ) : (
              <div className="sq-table-wrap">
                <table className="sq-table is-stacked cols-4">
                  <thead>
                    <tr>
                      <th scope="col">Store</th>
                      <th scope="col" className="num">Total sales</th>
                      <th scope="col" className="num">Transactions</th>
                      <th scope="col" className="num">Avg transaction</th>
                      <th scope="col" className="num">vs prior period</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.rows.map((row) => (
                      <tr
                        key={row.locationId}
                        className={row.locationId === worstLocationId ? "is-flagged" : undefined}
                      >
                        <td className="cell-title">
                          <div className="sq-strong">{row.name}</div>
                          {row.locationPin ? (
                            <div className="sq-muted">{row.locationPin}</div>
                          ) : null}
                        </td>
                        <td className="num" data-label="Sales">
                          {formatMoney(row.totalSales)}
                        </td>
                        <td className="num" data-label="Txns">
                          {row.transactionCount}
                        </td>
                        <td className="num" data-label="Avg">
                          {formatMoney(row.avgTransaction)}
                        </td>
                        <td
                          data-label="vs prior"
                          className={`num${
                            row.changePct == null
                              ? ""
                              : row.changePct < 0
                                ? " sq-value-bad"
                                : " sq-value-good"
                          }`}
                        >
                          {row.changePct != null && row.changePct < 0 ? (
                            <IconTrendingDown size={14} className="me-1" aria-hidden="true" />
                          ) : row.changePct != null && row.changePct > 0 ? (
                            <IconTrendingUp size={14} className="me-1" aria-hidden="true" />
                          ) : null}
                          {formatPct(row.changePct)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        ) : null}
      </div>
    </PageContainer>
  );
}
